import React, { useEffect, useState, useMemo, useCallback } from 'react';
import "./SubscriptionRequest.css"; // Add styles for the overlay
import axios from 'axios';

const apiRootUrl = process.env.REACT_APP_API_ROOT_ENDPOINT;

function SubscriptionRequest({ onClose, instance, accounts, loginRequest }) {
    const [formData, setFormData] = useState({
        userName: "",
        emailAddress: "",
        scheduleDay: "",
        scheduleTime: "",
        scheduleTimeZone: "",
        entities: "",
    });

    const [isSubmitted, setIsSubmitted] = useState(false); // State to track submission status

    // Memoize username and email to avoid recalculating on every render
    const { username, email } = useMemo(() => {
        const activeAccount = instance.getActiveAccount();
        if (accounts.length > 0 && activeAccount) {
            return {
                username: activeAccount.idTokenClaims?.name || "",
                email: activeAccount.idTokenClaims?.email || activeAccount.username,
            };
        }
        console.warn("No active account. Please log in.");
        return { username: "", email: "" };
    }, [accounts, instance]);

    // Update formData with username and email when they are available
    useEffect(() => {
        setFormData((prevFormData) => ({
            ...prevFormData,
            userName: username,
            emailAddress: email,
        }));
    }, [username, email]);

    // Use useCallback to memoize handleFormChange
    const handleFormChange = useCallback((event) => {
        const { name, value } = event.target;
        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: value,
        }));
    }, []);

    // Use useCallback to memoize handleSubmit
    const handleSubmit = useCallback(
        async (event) => {
            event.preventDefault();
            try {
                const activeAccount = instance.getActiveAccount();
                if (!activeAccount) {
                    console.warn("No active account. Please log in.");
                    return;
                }

                const tokenResponse = await instance.acquireTokenSilent({
                    ...loginRequest,
                    account: activeAccount,
                });

                const response = await axios.post(`${apiRootUrl}/v1/subscription-request`, formData, {
                    headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
                });

                if (response.status === 200) {
                    console.log("Form submitted successfully:", response.data);
                    setIsSubmitted(true); // Show "Request Submitted" message
                    setTimeout(() => {
                        setIsSubmitted(false); // Hide the message after 1.5 seconds
                        onClose(); // Close the overlay
                    }, 1500);
                } else {
                    console.error("Error submitting the form:", response.data);
                }
            } catch (error) {
                console.error("Error submitting the form:", error);
                alert("There was an error submitting the form. Please try again.");
            }
        },
        [formData, instance, loginRequest, onClose]
    );

    return (
        <div className="overlay">
            <div className="overlay-content">
                {isSubmitted ? (
                    <h2 id="success-message">Request Submitted</h2>
                ) : (
                    <>
                        <h2>Subscription Request</h2>
                        <p className="instructions">
                            Please fill out the form below to request a newsletter subscription to your email.
                            The newsletter contains shipping industry news summarized from Alphaliner&apos;s weekly newsletter.
                            The original Alphaliner pdf will be sent as well as an attachment.
                        </p>
                        <form onSubmit={handleSubmit}>
                            <label>
                                Name:
                                <input
                                    type="text"
                                    name="userName"
                                    value={formData.userName}
                                    autoComplete="off"
                                    required
                                    readOnly
                                />
                            </label>
                            <label>
                                Email:
                                <input
                                    type="email"
                                    name="emailAddress"
                                    value={formData.emailAddress}
                                    autoComplete="off"
                                    required
                                    readOnly
                                />
                            </label>
                            <label>
                                Schedule Day:
                                <select
                                    name="scheduleDay"
                                    value={formData.scheduleDay}
                                    onChange={handleFormChange}
                                    required
                                >
                                    <option value="" disabled>
                                        Select a day
                                    </option>
                                    <option value="Monday">Monday</option>
                                    <option value="Tuesday">Tuesday</option>
                                    <option value="Wednesday">Wednesday</option>
                                    <option value="Thursday">Thursday</option>
                                    <option value="Friday">Friday</option>
                                    <option value="Saturday">Saturday</option>
                                    <option value="Sunday">Sunday</option>
                                </select>
                            </label>
                            <label>
                                Schedule Time:
                                <input
                                    type="time"
                                    name="scheduleTime"
                                    value={formData.scheduleTime}
                                    onChange={handleFormChange}
                                    required
                                />
                            </label>
                            <label>
                                Schedule Time Zone:
                                <select
                                    name="scheduleTimeZone"
                                    value={formData.scheduleTimeZone}
                                    onChange={handleFormChange}
                                    required
                                >
                                    <option value="">Select a time zone</option>
                                    <option value="Pacific Standard Time">Pacific Standard Time (PST)</option>
                                    <option value="Mountain Standard Time">Mountain Standard Time (MST)</option>
                                    <option value="Central Standard Time">Central Standard Time (CST)</option>
                                    <option value="Eastern Standard Time">Eastern Standard Time (EST)</option>
                                    <option value="Atlantic Standard Time">Atlantic Standard Time (AST)</option>
                                    <option value="Newfoundland Standard Time">Newfoundland Standard Time (NST)</option>
                                    <option value="Greenwich Mean Time">Greenwich Mean Time (GMT)</option>
                                    <option value="Central European Time">Central European Time (CET)</option>
                                    <option value="Eastern European Time">Eastern European Time (EET)</option>
                                    <option value="India Standard Time">India Standard Time (IST)</option>
                                    <option value="China Standard Time">China Standard Time (CST)</option>
                                    <option value="Japan Standard Time">Japan Standard Time (JST)</option>
                                    <option value="Australian Eastern Standard Time">
                                        Australian Eastern Standard Time (AEST)
                                    </option>
                                    <option value="Australian Central Standard Time">
                                        Australian Central Standard Time (ACST)
                                    </option>
                                    <option value="Australian Western Standard Time">
                                        Australian Western Standard Time (AWST)
                                    </option>
                                    <option value="Hawaii-Aleutian Standard Time">Hawaii-Aleutian Standard Time (HST)</option>
                                    <option value="Alaska Standard Time">Alaska Standard Time (AKST)</option>
                                </select>
                            </label>
                            <label>
                                <p className="instructions">
                                    Please describe the content that you would like to see in your newsletter (customers, equipment
                                    types, locations, etc.):
                                </p>
                                <textarea
                                    name="entities"
                                    value={formData.entities}
                                    onChange={handleFormChange}
                                    autoComplete="off"
                                    placeholder="Top 5 lease customers..."
                                    required
                                />
                            </label>
                            <div className="form-buttons">
                                <button type="submit">Submit</button>
                                <button type="button" onClick={onClose}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}

export default SubscriptionRequest;